import { useEffect } from 'react'
import { generatePath, useHistory, useRouteMatch } from 'react-router-dom'
import {
  isAtLeastOneAccessFlagEnabled,
  isAtLeastOneDenyFlagEnabled,
} from 'app/navigation/helpers'
import type { StaticRoute, Subroute } from 'app/navigation/interfaces'
import { selectedCampaignPath } from 'app/navigation/paths'
import { accountsRoute, homeRoute } from 'app/navigation/staticRoutes'
import {
  adminRoutes,
  createContentRoutes,
  topNavStaticRoutes,
} from 'app/navigation/topNavStaticRoutes'
import { getIsUserTypeAuthorized } from 'app/router/routesUtils'
import cx from 'classnames'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { canAccessAccount, getIsAuthorized } from 'common/auth/permissions'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { BellNotification as BellIcon } from 'common/icons'
import { ReactComponent as Logo } from 'common/icons/logo/logo.svg'
import { ReactComponent as LogoSmall } from 'common/icons/logo/logo-small.svg'
import useProjectsQuery from 'features/campaigns/queries/useProjectsQuery'
import { onLoadPageCampaign } from 'features/campaigns/store/campaignSlice'

import NavbarLayout from '../navbarLayout/NavbarLayout'

import useGetUMVUsageQuery from './api/queries/useGetUMVUsageQuery'
import UMVProgressBar from './components/UMVProgressBar'
import AccountSelector from './AccountSelector'
import AvatarButton from './AvatarButton'
import CreateButton from './CreateButton'
import NavItems from './NavItems'
import ResponsiveMenu from './ResponsiveNavItem'

import navbarLayoutStyles from '../navbarLayout/navbarLayout.module.css'

const TopNavBar = () => {
  const dispatch = useAppDispatch()
  const history = useHistory()
  const flags = useFlags()
  useProjectsQuery()

  const { permissions, accounts, userType, accountId, accountCategory } =
    useAppSelector((state) => state.authStates)
  const { selectedCampaignId } = useAppSelector((state) => state.campaigns)
  const filterAuthorisedRoutes = (routes: StaticRoute[]) => {
    const availableStaticRoutes = routes.filter(
      ({
        accessPermission,
        accessFlags,
        denyFlags,
        accessLevel,
        shouldValidateAccountAccess,
      }) => {
        const isAuthorised = flags.rolesAndPermissions
          ? getIsAuthorized(permissions, accessPermission)
          : getIsUserTypeAuthorized(userType, accessLevel)

        const isAccessFlagEnabled =
          accessFlags &&
          accessFlags.flags.length > 0 &&
          accessFlags.type === 'disable'
            ? true
            : isAtLeastOneAccessFlagEnabled(flags, accessFlags?.flags)
        const isDeniedFlagEnabled =
          denyFlags &&
          denyFlags.flags.length > 0 &&
          denyFlags.type === 'disable'
            ? false
            : isAtLeastOneDenyFlagEnabled(flags, denyFlags?.flags)

        if (accessPermission && shouldValidateAccountAccess) {
          return (
            isAuthorised &&
            isAccessFlagEnabled &&
            !isDeniedFlagEnabled &&
            canAccessAccount(accountCategory, accessPermission)
          )
        }
        return isAuthorised && isAccessFlagEnabled && !isDeniedFlagEnabled
      }
    )

    let availableSubRoutes: Subroute[] | undefined
    const allAvailableRoutes = availableStaticRoutes.map((staticRoute) => {
      if (staticRoute?.subroutes) {
        availableSubRoutes = staticRoute.subroutes.filter(
          ({
            accessFlags,
            denyFlags,
            accessPermission,
            shouldValidateAccountAccess,
          }) => {
            const isAuthorized = flags.rolesAndPermissions
              ? getIsAuthorized(permissions, accessPermission)
              : true

            if (accessPermission && shouldValidateAccountAccess) {
              return (
                isAuthorized &&
                isAtLeastOneAccessFlagEnabled(flags, accessFlags?.flags) &&
                !isAtLeastOneDenyFlagEnabled(flags, denyFlags?.flags) &&
                canAccessAccount(accountCategory, accessPermission)
              )
            }
            return (
              isAuthorized &&
              isAtLeastOneAccessFlagEnabled(flags, accessFlags?.flags) &&
              !isAtLeastOneDenyFlagEnabled(flags, denyFlags?.flags)
            )
          }
        )
      }

      return {
        ...staticRoute,
        subroutes: availableSubRoutes?.length ? availableSubRoutes : undefined,
      }
    })

    const filterUndefinedSubRoutes = (
      routeName: string[],
      staticRoutes: StaticRoute[]
    ) =>
      staticRoutes.filter(
        ({ name, subroutes }) => !(routeName.includes(name) && !subroutes)
      )

    return filterUndefinedSubRoutes([adminRoutes.name], allAvailableRoutes)
  }

  const routeMatchCampaignId = useRouteMatch<{
    campaignId: string
  }>({
    path: [selectedCampaignPath],
  })

  useEffect(() => {
    if (routeMatchCampaignId) {
      const { campaignId } = routeMatchCampaignId.params

      if (!campaignId && selectedCampaignId) {
        history.push(
          generatePath(selectedCampaignPath, {
            accountId,
            campaignId: selectedCampaignId,
          })
        )
      }
      if (campaignId && selectedCampaignId !== campaignId) {
        dispatch(
          onLoadPageCampaign({
            campaignId,
          })
        )
      }
    }
  }, [dispatch, history, routeMatchCampaignId, selectedCampaignId, accountId])

  const topNavRoutes = [homeRoute, ...topNavStaticRoutes]

  const { data: umvData } = useGetUMVUsageQuery()

  return (
    <NavbarLayout variant="nav">
      <div className="flex items-center font-medium gap-6 h-9">
        <div className="hidden md:flex items-center">
          <Logo />
        </div>
        <ResponsiveMenu staticRoutes={filterAuthorisedRoutes(topNavRoutes)} />
        <div className="flex items-center md:hidden">
          <LogoSmall />
        </div>
        <CreateButton
          createContentRoutes={filterAuthorisedRoutes(createContentRoutes)}
        />
      </div>
      <div
        className={cx(
          'flex items-center justify-center',
          navbarLayoutStyles.navbarCenter
        )}
      >
        <NavItems staticRoutes={filterAuthorisedRoutes(topNavRoutes)} />
      </div>
      <div className="flex gap-2 h-10 font-medium justify-end items-center min-w-0">
        {flags.enableUmvUsageTracking && umvData?.umvValid && (
          <div className="w-50 md:w-25 lg:w-50">
            <UMVProgressBar />
          </div>
        )}
        <AccountSelector
          accountsRoute={accountsRoute}
          accounts={accounts || []}
        />
        {flags.showContent && (
          <BellIcon className="w-6 h-6 hover:text-gray-100" />
        )}
        <AvatarButton />
      </div>
    </NavbarLayout>
  )
}

export default TopNavBar
